import { useState, useEffect, useRef } from 'react';
import DualDate from '../../components/common/DualDate';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { SkeletonRow } from '../../components/common/Skeleton';
import { getAdminUsers, updateAdminStatus, sendPasswordReset } from '../../services/adminService';
import useImpersonation from '../../hooks/useImpersonation';
import {
  MdPersonAdd, MdClose, MdCheckCircle, MdCancel, MdShield, MdEdit,
  MdBlock, MdLockReset, MdEmail, MdPhone, MdCalendarToday, MdSearch, MdRemoveRedEye, MdExpandMore,
} from 'react-icons/md';
import InviteModal from '../../components/common/InviteModal';
import EditAdminModal from '../../components/admin/EditAdminModal';
import PendingInvitationsPanel from '../../components/common/PendingInvitationsPanel';
import { getDisplayTxId } from '../../utils/displayId';
import useAuth from '../../hooks/useAuth';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// An admin row is the Primary Admin if adminType says so, or (fallback, when the
// flag is missing) it's the viewer's own row — this page is only reachable by the
// Primary Admin, so the current user is the club's primary.
const isPrimaryAdmin = (admin, currentUser) =>
  admin?.adminType === 'primary' ||
  (!!admin?._id && admin._id === currentUser?._id &&
    (currentUser?.isPrimaryAdmin === true || currentUser?.adminType === 'primary'));

// Small Primary / Staff pill
function AdminTypeBadge({ primary }) {
  return primary ? (
    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">Primary</span>
  ) : (
    <span className="text-[10px] bg-gray-100 text-muted px-1.5 py-0.5 rounded-full font-semibold">Staff</span>
  );
}

// ── Admin Detail Drawer ──────────────────────────────────

function AdminDrawer({ admin, onClose, onUpdated, isCurrentUser, isPrimary, viewerIsPrimary }) {
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen,      setEditOpen]      = useState(false);
  // Editing another admin needs the Primary Admin; editing your own row is always allowed
  // (backend enforces the same). Hidden entirely in read-only support mode.
  const canEdit = !isReadOnly && (isCurrentUser || viewerIsPrimary);

  const statusMutation = useMutation({
    mutationFn: ({ isActive }) => updateAdminStatus(admin._id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(`Admin ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update status'),
  });

  const resetMutation = useMutation({
    mutationFn: () => sendPasswordReset(admin._id),
    onSuccess: () => toast.success('Password reset email sent'),
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to send reset email'),
  });

  const isActive = admin.isActive;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-sm bg-card h-full flex flex-col shadow-2xl relative">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary text-sm">Admin Details</h2>
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                onClick={() => setEditOpen(true)}
                title="Edit Details"
                className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-nav-hover transition-colors"
              >
                <MdEdit className="text-base" /> Edit
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Profile card — display only (admins change their OWN photo via Settings) */}
          <div className="flex items-center gap-4">
            {admin.profilePhoto ? (
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <img src={admin.profilePhoto} alt={admin.name || 'Admin'} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-16 h-16 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                {(admin.name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-bold text-secondary">{admin.name}</p>
                {isCurrentUser && (
                  <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full font-semibold">You</span>
                )}
                <AdminTypeBadge primary={isPrimary} />
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Contact info */}
          <div className="space-y-0">
            <div className="flex items-center gap-3 py-2.5 border-b border-divider">
              <MdEmail className="text-subtle text-lg flex-shrink-0" />
              <div>
                <p className="text-xs text-subtle">Email</p>
                <p className="text-sm text-secondary">{admin.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 py-2.5 border-b border-divider">
              <MdPhone className="text-subtle text-lg flex-shrink-0" />
              <div>
                <p className="text-xs text-subtle">Phone</p>
                <p className="text-sm text-secondary">{admin.phone || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 py-2.5">
              <MdCalendarToday className="text-subtle text-lg flex-shrink-0" />
              <div>
                <p className="text-xs text-subtle">Added on</p>
                <p className="text-sm text-secondary"><DualDate date={admin.createdAt} /></p>
              </div>
            </div>
          </div>

          {/* Role badge */}
          <div className="flex items-center gap-2 bg-purple-50 rounded-xl px-4 py-3">
            <MdShield className="text-purple-500 text-lg" />
            <div>
              <p className="text-xs text-muted">Role</p>
              <p className="text-sm font-semibold text-purple-700">
                {isPrimary ? 'Primary Admin' : 'Staff Admin'}
              </p>
            </div>
          </div>

          {/* Primary admins cannot be deactivated or reset by anyone here. */}
          {isPrimary && !isCurrentUser && (
            <div className="bg-amber-50 rounded-xl px-4 py-3 text-center">
              <p className="text-xs text-amber-700">The Primary Admin cannot be deactivated or modified here.</p>
            </div>
          )}

          {/* Actions */}
          {!isCurrentUser && !isPrimary && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-subtle uppercase tracking-wide mb-2">Actions</p>
              <button
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="w-full flex items-center gap-2 px-4 py-2.5 border border-divider rounded-xl text-sm text-secondary hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdLockReset className="text-blue-500 text-lg" />
                {resetMutation.isPending ? 'Sending…' : 'Send Password Reset Email'}
              </button>
              <button
                onClick={() => statusMutation.mutate({ isActive: !isActive })}
                disabled={statusMutation.isPending || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className={`w-full flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                  ${isActive ? 'border-red-200 text-red-600 hover:bg-nav-hover' : 'border-green-200 text-green-600 hover:bg-nav-hover'}`}
              >
                {isActive ? <MdBlock className="text-lg" /> : <MdCheckCircle className="text-lg" />}
                {statusMutation.isPending ? 'Updating…' : isActive ? 'Deactivate Account' : 'Activate Account'}
              </button>
            </div>
          )}

          {isCurrentUser && (
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-center">
              <p className="text-xs text-subtle">Actions are disabled for your own account</p>
            </div>
          )}
        </div>

        {editOpen && (
          <EditAdminModal
            admin={admin}
            onClose={() => setEditOpen(false)}
            onSaved={onUpdated}
          />
        )}
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────

function Admins() {
  document.title = 'Admins — TrainAxis';
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;

  // Admin management is restricted to the Primary Admin. Staff Admins reaching
  // this route directly get a polite block. (Backend enforces all actions too.)
  const isStaffAdmin = currentUser?.role === 'admin' &&
    (currentUser?.isPrimaryAdmin === false || currentUser?.adminType === 'staff');
  const [inviteOpen,     setInviteOpen]     = useState(false);
  const [selectedAdmin,  setSelectedAdmin]  = useState(null);
  const [page,           setPage]           = useState(1);
  const [limit,          setLimit]          = useState(10);
  const [sortBy,         setSortBy]         = useState('createdAt');
  const [sortOrder,      setSortOrder]      = useState('desc');
  const [search,         setSearch]         = useState('');
  const [searchInput,    setSearchInput]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const debounceRef                         = useRef(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['admin-users', page, limit, sortBy, sortOrder, search, statusFilter],
    queryFn:  () => getAdminUsers({
      role: 'admin', page, limit, sortBy, sortOrder,
      search,
      isActive: statusFilter,
    }).then(r => r.data),
    placeholderData: (prev) => prev,
  });

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateAdminStatus(id, isActive),
    onSuccess:  () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update status'),
  });

  const admins = data?.users || [];
  const total  = data?.total || 0;
  const pages  = data?.pages || 1;

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };
  const sortIcon = (field) => (sortBy !== field ? '↕' : sortOrder === 'asc' ? '↑' : '↓');

  // If deactivating empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && admins.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, admins.length, page]);

  // Staff Admins cannot manage admins — polite block on direct navigation.
  if (isStaffAdmin) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <MdShield className="text-purple-400 text-3xl" />
        </div>
        <h2 className="text-lg font-bold text-secondary mb-1">Admin management is restricted</h2>
        <p className="text-sm text-subtle mb-6">
          Only the club's Primary Admin can manage other admins. You still have full
          access to students, trainers, classes, payments, and the rest of your dashboard.
        </p>
        <Link
          to="/admin/dashboard"
          className={`inline-flex items-center gap-2 px-5 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} font-medium text-sm rounded-full hover:opacity-90 transition-all duration-200`}
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  // Club access blocked (subscription suspended/expired) — clear panel, not "0 admins".
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
        <div>
          <p className="text-subtle text-sm">{total} admin{total !== 1 ? 's' : ''} registered</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="inverted"
            onClick={() => setInviteOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
            <MdPersonAdd className="text-lg" /> Add Admin
          </Button>
        </div>
      </div>
      <p className="text-xs text-subtle mb-6 max-w-2xl leading-relaxed">
        Only the Primary Admin can invite or manage club admins. Staff Admins can manage daily
        club operations but cannot manage admins.
      </p>

      {/* Filters */}
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[180px] relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-lg" />
            <input
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by name, email, phone, or TrainAxis ID…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-divider rounded-full focus:outline-none focus:ring-2 focus:ring-divider"
            />
          </div>
          <div className="relative">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm truncate focus:outline-none focus:ring-2 focus:ring-divider"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
          </div>
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-xs text-subtle">Rows</span>
            <div className="relative">
            <select
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm truncate focus:outline-none focus:ring-2 focus:ring-divider"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-gray-50 border-b border-divider text-xs text-subtle font-semibold uppercase tracking-wide">
              <th className="text-left px-5 py-3">
                <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                  Admin <span className="text-[10px]">{sortIcon('name')}</span>
                </button>
              </th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Phone</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">
                <button onClick={() => toggleSort('createdAt')} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                  Added <span className="text-[10px]">{sortIcon('createdAt')}</span>
                </button>
              </th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {isPending
              ? Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} cols={5} />)
              : !admins.length
                ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16 text-subtle text-sm">
                      <MdShield className="text-4xl text-subtle mx-auto mb-3" />
                      {(search || statusFilter) ? 'No admins match your filters' : 'No admins found'}
                    </td>
                  </tr>
                )
                : admins.map(a => {
                  const isCurrentUser = a._id === currentUser?._id;
                  const isPrimary     = isPrimaryAdmin(a, currentUser);
                  return (
                    <tr key={a._id} onClick={() => setSelectedAdmin(a)} className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors cursor-pointer">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          {a.profilePhoto ? (
                            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                              <img src={a.profilePhoto} alt={a.name || 'Admin'} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-9 h-9 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {(a.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-medium text-secondary truncate">{a.name}</p>
                              {isCurrentUser && (
                                <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full font-semibold">You</span>
                              )}
                              <AdminTypeBadge primary={isPrimary} />
                            </div>
                            <span className="inline-block mt-0.5 text-[10px] font-mono font-semibold text-muted bg-gray-100 px-1.5 py-0.5 rounded">
                              {getDisplayTxId(a)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-sm text-muted">
                        {a.phone || '—'}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-muted whitespace-nowrap">
                        <DualDate date={a.createdAt} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {a.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            title="View Profile"
                            onClick={(e) => { e.stopPropagation(); setSelectedAdmin(a); }}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-secondary transition-colors">
                            <MdRemoveRedEye className="text-lg" />
                          </button>
                          {(isCurrentUser || isPrimary) ? (
                            <span className="text-xs text-subtle">—</span>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); statusMutation.mutate({ id: a._id, isActive: !a.isActive }); }}
                              disabled={statusMutation.isPending || isReadOnly}
                              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                              className="flex items-center gap-1 text-xs font-medium text-muted hover:text-secondary hover:bg-nav-hover rounded-full px-2 py-1 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {a.isActive
                                ? <><MdCancel className="text-base" /> Deactivate</>
                                : <><MdCheckCircle className="text-base" /> Activate</>
                              }
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-divider gap-3 flex-wrap">
            <p className="text-xs text-subtle">
              Showing {(page - 1) * limit + 1}–{(page - 1) * limit + admins.length} of {total} · Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <Button variant="ghostPremium" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
              <Button variant="ghostPremium" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>Next →</Button>
            </div>
          </div>
        )}
      </Card>

      <PendingInvitationsPanel role="admin" />

      {inviteOpen && (
        <InviteModal
          lockedRole="admin"
          onClose={() => setInviteOpen(false)}
          note="Admins are added via invitation only for security. They will set their own password."
        />
      )}

      {selectedAdmin && (
        <AdminDrawer
          admin={selectedAdmin}
          onClose={() => setSelectedAdmin(null)}
          onUpdated={(u) => u && setSelectedAdmin(prev => (prev ? { ...prev, ...u } : prev))}
          isCurrentUser={selectedAdmin._id === currentUser?._id}
          isPrimary={isPrimaryAdmin(selectedAdmin, currentUser)}
          viewerIsPrimary={currentUser?.isPrimaryAdmin === true || currentUser?.adminType === 'primary'}
        />
      )}
    </div>
  );
}

export default Admins;
